import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { catalogAdmin, catalog } from '../api/index.js';
import { Badge, statusBadge } from '../components/ui/badge.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.js';
import { Loader2, AlertCircle, Tag } from 'lucide-react';
import { format } from 'date-fns';

function fmtDate(v: unknown): string {
  if (!v || typeof v !== 'string') return '—';
  try { return format(new Date(v), 'MMM d, yyyy'); } catch { return v as string; }
}

export function GenreDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: genre, isLoading, error } = useQuery({
    queryKey: ['genre-detail', id],
    queryFn: () => catalogAdmin.genreDetail(id!),
    enabled: !!id,
  });

  const { data: itemsData } = useQuery({
    queryKey: ['catalog-items-by-genre', id],
    queryFn: () => catalog.items({ advancedFilters: [{ field: 'genres', op: 'contains', value: id! }], limit: 20, sortBy: 'releaseAt', sortDir: 'desc' }),
    enabled: !!id && !!genre,
    staleTime: 2 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Loader2 className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error || !genre) {
    return (
      <div className="p-8 flex items-center gap-3 text-red-600">
        <AlertCircle size={20} />
        <p>{error instanceof Error ? error.message : 'Genre not found'}</p>
      </div>
    );
  }

  const items = itemsData?.items ?? [];
  const total = itemsData?.total ?? 0;

  return (
    <div className="p-8 max-w-3xl mx-auto space-y-6">
      <div>
        <Link to="/genres" className="text-sm text-brand-600 hover:underline mb-2 inline-block">← Back to Genres</Link>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
            <Tag size={20} className="text-gray-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{genre.name}</h1>
            <p className="text-xs text-gray-400 font-mono mt-0.5">{id}</p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Details</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-1.5 text-sm">
            <div className="flex gap-4 py-1.5 border-b border-gray-50">
              <span className="w-24 text-gray-500 flex-shrink-0">Name</span>
              <span className="text-gray-900">{genre.name}</span>
            </div>
            {genre.slug && (
              <div className="flex gap-4 py-1.5 border-b border-gray-50">
                <span className="w-24 text-gray-500 flex-shrink-0">Slug</span>
                <span className="text-gray-900 font-mono text-xs">{genre.slug}</span>
              </div>
            )}
            <div className="flex gap-4 py-1.5">
              <span className="w-24 text-gray-500 flex-shrink-0">ID</span>
              <span className="text-gray-400 font-mono text-xs">{genre._id}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {total > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Items ({total})</CardTitle>
              <Link to={`/catalog?adv=${encodeURIComponent(JSON.stringify([{ field: 'genres', op: 'contains', value: id }]))}`} className="text-xs text-brand-600 hover:underline">View all →</Link>
            </div>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-medium text-gray-400 uppercase pb-2">Title</th>
                  <th className="text-left text-xs font-medium text-gray-400 uppercase pb-2">Status</th>
                  <th className="text-left text-xs font-medium text-gray-400 uppercase pb-2">Release</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map(item => (
                  <tr
                    key={item._id}
                    onClick={() => navigate(`/catalog/${item._id}`)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="py-2 pr-4 font-medium text-gray-900 truncate max-w-[16rem]">{item.name}</td>
                    <td className="py-2 pr-4">
                      {item.status ? <Badge variant={statusBadge(item.status)}>{item.status}</Badge> : '—'}
                    </td>
                    <td className="py-2 text-gray-500 text-xs">{fmtDate(item.releaseAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {total > 20 && (
              <p className="text-xs text-gray-400 mt-3">Showing 20 of {total}.</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
